import type { WhatsappChannel, WhatsappQr } from '@dnx/contracts';
import { Alert, Badge, Button, Card, CardHeader, Dialog, Field, Input, Spinner } from '@dnx/ui';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { CircleCheck, Plus, QrCode, Smartphone, Unplug } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';

const channelsQuery = (orgId: string) => queryOptions({
  queryKey: ['platform', 'orgs', orgId, 'whatsapp'],
  queryFn: () => api<WhatsappChannel[]>(`/platform/organizations/${orgId}/whatsapp`),
});

const STATUS: Record<WhatsappChannel['status'], { label: string; tone: 'success' | 'warning' | 'danger' | 'neutral' }> = {
  connected: { label: 'Conectado', tone: 'success' },
  pending: { label: 'Aguardando leitura do QR', tone: 'warning' },
  disconnected: { label: 'Desconectado', tone: 'neutral' },
  banned: { label: 'Bloqueado pelo WhatsApp', tone: 'danger' },
};
const QR_REFRESH_MS = 35_000; // o QR do WhatsApp expira em ~40 s
const STATE_POLL_MS = 3_000;

/** Janela do QR: mostra, renova antes de expirar e fecha sozinha quando o celular conecta. */
function QrDialog({ orgId, initial, onClose }: { orgId: string; initial: WhatsappQr; onClose: () => void }) {
  const qc = useQueryClient();
  const [qr, setQr] = useState(initial.qr);
  const [channel, setChannel] = useState(initial.channel);
  const [error, setError] = useState<string | null>(null);
  const connected = channel.status === 'connected';

  useEffect(() => {
    if (connected) { void qc.invalidateQueries({ queryKey: ['platform', 'orgs', orgId, 'whatsapp'] }); return; }
    const poll = setInterval(async () => {
      try { setChannel(await api<WhatsappChannel>(`/platform/organizations/${orgId}/whatsapp/${channel.id}/state`)); setError(null); }
      catch (e) { setError((e as Error).message); }
    }, STATE_POLL_MS);
    const renew = setInterval(async () => {
      try { const r = await api<WhatsappQr>(`/platform/organizations/${orgId}/whatsapp/${channel.id}/qr`); setQr(r.qr); setChannel(r.channel); }
      catch (e) { setError((e as Error).message); }
    }, QR_REFRESH_MS);
    return () => { clearInterval(poll); clearInterval(renew); };
  }, [connected, channel.id, orgId, qc]);

  return (
    <Dialog open onClose={onClose} title={connected ? 'Número conectado' : `Conectar “${channel.displayName}”`}
      description={connected ? undefined : 'Use o celular com o WhatsApp da clínica (de preferência WhatsApp Business).'}>
      <div className="p-5">
        {connected ? (
          <div className="flex flex-col items-center py-6 text-center">
            <span className="grid size-14 place-items-center rounded-full bg-success-soft text-success"><CircleCheck className="size-7" /></span>
            <p className="mt-4 font-semibold text-fg">Pronto: {channel.displayName}{channel.phoneLast4 ? ` (final ${channel.phoneLast4})` : ''}</p>
            <p className="mt-1 text-sm text-fg-muted">As conversas desse número passam a chegar no painel da clínica, em Conversas.</p>
            <Button className="mt-6" onClick={onClose}>Fechar</Button>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-[240px_1fr] sm:items-center">
            <div className="grid size-60 place-items-center rounded-xl border border-border bg-white p-3">
              {qr.base64 ? <img src={qr.base64} alt="QR code para conectar o WhatsApp" className="size-full" /> : <Spinner className="size-6 text-fg-subtle" label="Gerando QR" />}
            </div>
            <ol className="space-y-3 text-sm text-fg">
              <li><strong>1.</strong> Abra o WhatsApp no celular da clínica.</li>
              <li><strong>2.</strong> Toque em <strong>Configurações</strong> (ou nos três pontos) → <strong>Aparelhos conectados</strong>.</li>
              <li><strong>3.</strong> Toque em <strong>Conectar um aparelho</strong> e aponte a câmera para este QR.</li>
              {qr.pairingCode && (
                <li className="text-fg-muted">Sem câmera? Escolha “conectar com número de telefone” e digite o código <code className="rounded bg-surface-muted px-1.5 py-0.5 font-mono text-fg">{qr.pairingCode}</code>.</li>
              )}
              <li className="flex items-center gap-2 text-[13px] text-fg-muted"><Spinner className="size-3.5" label="" />Aguardando o celular… o QR se renova sozinho.</li>
            </ol>
          </div>
        )}
        {error && <Alert tone="danger" className="mt-4">{error}</Alert>}
      </div>
    </Dialog>
  );
}

/** Console → clínica → WhatsApp: números conectados à Evolution API e QR para conectar. */
export function WhatsappTab({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const { data: channels, isLoading } = useQuery(channelsQuery(orgId));
  const [name, setName] = useState('WhatsApp Recepção');
  const [open, setOpen] = useState<WhatsappQr | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: ['platform', 'orgs', orgId, 'whatsapp'] });

  const create = useMutation({
    mutationFn: () => api<WhatsappQr>(`/platform/organizations/${orgId}/whatsapp`, { method: 'POST', body: { displayName: name } }),
    onSuccess: (r) => { setOpen(r); refresh(); },
  });
  const showQr = useMutation({
    mutationFn: (id: string) => api<WhatsappQr>(`/platform/organizations/${orgId}/whatsapp/${id}/qr`),
    onSuccess: (r) => setOpen(r),
  });
  const disconnect = useMutation({
    mutationFn: (id: string) => api<WhatsappChannel>(`/platform/organizations/${orgId}/whatsapp/${id}/disconnect`, { method: 'POST' }),
    onSettled: refresh,
  });
  const [confirm, setConfirm] = useState<string | null>(null);
  const error = create.error ?? showQr.error ?? disconnect.error;

  return (
    <div className="grid gap-5">
      <Card>
        <CardHeader title="Números de WhatsApp" description="Cada número é uma instância na Evolution API. As mensagens chegam no painel da clínica, em Conversas." />
        <div className="p-5">
          {error && <Alert tone="danger" className="mb-4">
            {error.message}{' '}
            {/Configurações/.test(error.message) && <Link to="/ajustes" className="font-medium underline">Abrir Configurações</Link>}
          </Alert>}
          {isLoading ? <Spinner className="size-5 text-fg-subtle" label="Carregando" /> : channels?.length ? (
            <ul className="divide-y divide-border rounded-lg border border-border">
              {channels.map((c) => (
                <li key={c.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3.5">
                  <span className="grid size-10 place-items-center rounded-full bg-surface-muted text-fg-muted"><Smartphone className="size-5" /></span>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-fg">{c.displayName}{c.phoneLast4 && <span className="ml-2 text-[13px] font-normal text-fg-muted">final {c.phoneLast4}</span>}</p>
                    <p className="truncate font-mono text-[12px] text-fg-subtle">{c.provider === 'dev' ? 'simulado (dev)' : c.instanceName}</p>
                  </div>
                  <Badge tone={STATUS[c.status].tone} className="whitespace-nowrap">{STATUS[c.status].label}</Badge>
                  {c.provider === 'evolution' && (
                    <div className="flex gap-1.5">
                      {c.status !== 'connected' && (
                        <Button size="sm" variant="secondary" loading={showQr.isPending && showQr.variables === c.id} onClick={() => showQr.mutate(c.id)}>
                          <QrCode className="size-4" />{c.status === 'disconnected' ? 'Reconectar' : 'Mostrar QR'}
                        </Button>
                      )}
                      {c.status === 'connected' && (confirm === c.id
                        ? <>
                          <Button size="sm" variant="danger" loading={disconnect.isPending} onClick={() => { disconnect.mutate(c.id); setConfirm(null); }}>Desconectar</Button>
                          <Button size="sm" variant="ghost" onClick={() => setConfirm(null)}>Manter</Button>
                        </>
                        : <Button size="sm" variant="ghost" onClick={() => setConfirm(c.id)}><Unplug className="size-4" />Desconectar</Button>)}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          ) : <p className="text-sm text-fg-muted">Nenhum número ainda. Conecte o WhatsApp da clínica abaixo.</p>}

          <form className="mt-5 flex flex-wrap items-end gap-3 border-t border-border pt-5" onSubmit={(e) => { e.preventDefault(); if (name.trim().length >= 2) create.mutate(); }}>
            <div className="min-w-[240px] flex-1">
              <Field label="Nome do número" hint="Como a equipe vai ver este número (ex.: WhatsApp Recepção, Comercial).">
                <Input value={name} onChange={(e) => setName(e.target.value)} />
              </Field>
            </div>
            <Button type="submit" loading={create.isPending} disabled={name.trim().length < 2}><Plus className="size-4" />Conectar número</Button>
          </form>
        </div>
      </Card>
      {open && <QrDialog orgId={orgId} initial={open} onClose={() => { setOpen(null); refresh(); }} />}
    </div>
  );
}
